// AtAGlance.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Users
} from 'lucide-react';
import { MdFolderCopy } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const AtAGlance = ({ 
  userType, 
  selectedLabId, 
  selectedLabName, 
  userName 
}) => {
  const navigate = useNavigate();
  
  // Accounts state
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labId, setLabId] = useState(null);
  const [ready, setReady] = useState(false);
  const [usersMap, setUsersMap] = useState({}); // Store users by labId

  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get LabId and User Type from localStorage
  useEffect(() => {
    const storedLabId = localStorage.getItem('userLabId');
    const storedUserType = localStorage.getItem('userType');
    
    if (storedLabId) {
      setLabId(storedLabId);
    }
    
    setReady(true);
  }, []);

  // Fetch users first, then fetch accounts
  useEffect(() => {
    if (ready) {
      fetchUsersAndAccounts();
    }
  }, [ready, labId]);

  // Fetch users to create a mapping of labId -> labName
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      
      if (!token) {
        console.warn('No authentication token found for users fetch');
        return {};
      }

      const url = `${process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com'}/api/auth/users-by-lab`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch users');
      }

      if (data.success && Array.isArray(data.users)) {
        // Create a map of labId -> labName from users
        const userLabMap = {};
        data.users.forEach(user => {
          if (user.labId) {
            userLabMap[user.labId] = user.labName || user.labId;
          }
        });
        return userLabMap;
      }
      return {};
    } catch (err) {
      console.error('Error fetching users:', err);
      return {};
    }
  };

  // Main function to fetch users and accounts
  const fetchUsersAndAccounts = async () => {
    setAccountsLoading(true);
    try {
      // First fetch users to get lab names mapping
      const userLabMapping = await fetchUsers();
      setUsersMap(userLabMapping);

      // Then fetch accounts
      let allAccounts = [];
      
      if (userType === 'lab' && labId) {
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
        );
        
        if (response.data.success) {
          allAccounts = (response.data.data || [])
            .filter((account) => account && account.account_name)
            .map((account) => ({
              ...account,
              _id: account._id || account.accountId,
              account_name: account.account_name || "Unnamed",
              status: account.status || "Active",
              addedBy: account.addedBy || "Unknown",
              source: "HMS",
              createdAt: account.createdAt || new Date().toISOString(),
            }));
        }
      } else {
        const [hmsData, ddxData] = await Promise.all([
          fetchAllHMSAccounts(),
          fetchDDXAccounts(),
        ]);
        allAccounts = [...hmsData, ...ddxData];
      }
      
      // Filter only active accounts, sort by createdAt descending, and take only 10
      const activeAccounts = allAccounts
        .filter(account => 
          account.status?.toLowerCase() === 'active' || 
          account.status?.toLowerCase() === 'active (ddx)'
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10);
      
      setRows(activeAccounts);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchAllHMSAccounts = async () => {
    try {
      const response = await axios.get(
        "https://hms.backend.hiragroupcloud.com/api/account/account"
      );
      
      const normalized = (response.data.data || [])
        .filter((account) => account && account.account_name)
        .map((account) => ({
          ...account,
          _id: account._id || account.accountId,
          account_name: account.account_name || "Unnamed",
          status: account.status || "Active",
          addedBy: account.addedBy || "Unknown",
          source: "HMS",
          createdAt: account.createdAt || new Date().toISOString(),
        }));
      return normalized;
    } catch (error) {
      console.error("Error fetching HMS data:", error);
      return [];
    }
  };

  const fetchDDXAccounts = async () => {
    try {
      const response = await axios.get("https://hms.backend.hiragroupcloud.com/api/users");

      const normalized = (response.data.users || []).map((user) => ({
        _id: user._id,
        account_name: user.practiceName || `${user.firstName} ${user.lastName}`,
        status: user.status || "Active (DDX)",
        addedBy: user.addedBy || "DDX System",
        source: "DDX",
        createdAt: user.createdAt || new Date().toISOString(),
      }));
      return normalized;
    } catch (error) {
      console.error("Error fetching DDX data:", error);
      return [];
    }
  };

  // Function to get lab name from addedBy (which is a labId)
  const getLabNameFromAddedBy = (addedBy) => {
    if (!addedBy) return 'Unknown';
    
    // Check if we have the lab name in our users map
    if (usersMap[addedBy]) {
      return usersMap[addedBy];
    }
    
    // If addedBy is "DDX System", return as is
    if (addedBy === "DDX System") return addedBy;
    
    // Return the labId if we can't find the name
    return `Lab: ${addedBy}`;
  };

  const filteredRows = rows.filter((row) =>
    Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Columns definition - Only Account Name, Status, and Created By
  const columns = [
    { 
      field: "account_name", 
      headerName: "Account Name", 
      width: 400,
      flex: 1,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: '#dbeafe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={16} color="#004aad" />
          </div>
          <span style={{ fontWeight: '500' }}>{params.value}</span>
        </div>
      )
    },
    { 
      field: "status", 
      headerName: "Status", 
      width: 150,
      renderCell: (params) => (
        <span style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: '500',
          border: '1px solid #c3e6cb',
        }}>
          Active
        </span>
      )
    },
    { 
      field: "addedBy", 
      headerName: "Created By", 
      width: 250,
      flex: 1,
      renderCell: (params) => {
        const labName = getLabNameFromAddedBy(params.value);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
       
            <span style={{ color: '#334155', fontSize: '0.85rem' }}>
              {labName}
            </span>
          </div>
        );
      }
    },
  ];

  return (
    <div ref={containerRef} style={{
      borderRadius: '16px',
      padding: isMobile ? '1rem' : '1.5rem',
      position: 'relative',
      background: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{
        background: '#004aad',
        color: 'white',
        padding: isMobile ? '10px 16px' : '12px 20px',
        borderRadius: '12px 12px 0 0',
        margin: isMobile ? '-1rem -1rem 1rem -1rem' : '-1.5rem -1.5rem 1.5rem -1.5rem',
        fontWeight: '600',
        fontSize: isMobile ? '1rem' : '1.1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MdFolderCopy size={isMobile ? 18 : 20} /> 
          <span>
            Recent Active Accounts
            {userType === 'lab' ? (
              selectedLabId === 'all' ? ' (All Labs)' : ` (${selectedLabName || selectedLabId})`
            ) : userType !== 'admin' ? ` (${userName})` : ''}
          </span>
        </div>
      </h2>

      <div style={{ padding: "10px" }}>
        <div style={{
          width: "100%",
          backgroundColor: "#f5f5f5",
          padding: "15px",
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
          {accountsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </div>
          ) : error ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px', 
              color: '#ef4444',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              margin: '20px'
            }}>
              {error}
            </div>
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row._id}
              autoHeight
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10]}
              disableRowSelectionOnClick
              sx={{
                backgroundColor: "#fff",
                borderRadius: "5px",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f0f0f0",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ddd",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "visible !important",
                },
              }}
            />
          )}
        </div>
        
        {!accountsLoading && rows.length === 0 && !error && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#64748b',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            <Users size={48} color="#94a3b8" style={{ marginBottom: '10px' }} />
            <p>No active accounts found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AtAGlance;